import { useCompanies } from "@/features/companies/hooks/useCompanies";
import { useCompanyEquipments } from "../hooks/queries/useCompanyEquipments";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { useUsers } from "@/features/users/hooks/useUsers";
import { useCompanyUsers } from "../hooks/queries/useCompanyUsers";
import { useEstablishmentUsers } from "../hooks/queries/useEstablishmentUsers";
import { useCards } from "@/features/cards/hooks/useCards";
import { BoxesIcon, BriefcaseBusinessIcon, Building2Icon, BuildingIcon, CarFrontIcon, CoinsIcon, DollarSign, FileBoxIcon, FileSpreadsheetIcon, FileTextIcon, ForkliftIcon, FuelIcon, GlobeLockIcon, PiggyBankIcon, PlugIcon, PrinterIcon, TablePropertiesIcon, TicketIcon, User2Icon, WalletCardsIcon } from "lucide-react";
import { useClientBills } from "@/features/clientBills/hooks/useClientBills";
import { useEstablishmentBills } from "@/features/establishmentBills/hooks/useEstablishmentBills";
import { usePermittedReports } from "@/features/reports/hooks/usePermittedReports";
import { usePrinterMachines } from "../hooks/queries/usePrinterMachines";
import { usePosMachines } from "@/features/pos/hooks/usePosMachines";
import { useFeeTemplates } from "@/features/feeTemplates/hooks/useFeeTemplates";
import { useProducts } from "../hooks/queries/useProducts";
import { EquipmentsTransformer } from "../services/equipmentTransformer";
import { CompaniesTransformer } from "@/features/companies/services/companiesTransformer";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import { UsersTransformer } from "@/features/users/services/usersTransformer";
import { SearchableModel } from "../types/searchableModel.type";
import { CompanyUsersTransformer } from "../services/companyUsersTransformer";
import { EstablishmentUsersTransformer } from "../services/establishmentUsersTransformer";
import { AllCardsTransformer } from "@/features/cards/services/allCardsTransformer";
import { ClientBillsTransformer } from "@/features/clientBills/services/clientBillsTransformer";
import { EstablishmentBillsTransformer } from "@/features/establishmentBills/services/establishmentBillsTransformer";
import { PermittedReportsTransformer } from "@/features/reports/services/permittedReportsTransformer";
import { PrinterMachinesTransformer } from "@/features/printerMachines/services/printerMachinesTransformer";
import { PosTransformer } from "@/features/pos/services/posTransformer";
import { FeeTemplatesTransformer } from "@/features/feeTemplates/services/feeTemplatesTransformer";
import { ProductsTransformer } from "@/features/products/services/productsTransformer";
import { useProductCategories } from "../hooks/queries/useProductCategories";
import { ProductCategoryTransformer } from "@/features/productCategories/services/productCategoryTransformer";
import { useEquipmentModels } from "@/features/equipmentModels/hooks/useEquipmentModels";
import { EquipmentModelsTransformer } from "@/features/equipmentModels/services/equipmentModelsTransformer";
import { useBillingEventsWithFiscalDocumentRequirement } from "@/features/billing/hooks/useBillingEventWithFiscalDocumentRequirement";
import { BillingEventsFiscalDocumentsTransformer } from "@/features/billing/services/eventsFiscalDocumentsTransformer";
import { useBillingFiscalDocumentGroups } from "@/features/billing/hooks/useBillingFiscalDocumentGroups";
import { BillingFiscalDocumentGroupsTransformer } from "@/features/billing/services/fiscalDocumentGroupsTransformer";
import usePrivateCompanyLeads from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeads";
import { privateCompanyLeadsQuickSearchModelTransformer } from "@/features/privateCompanyLeadsFunnel/services/privateCompanyLeadsTransformer";
import { usePublicCompanyLeads } from "@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLeads";
import { publicCompanyLeadsQuickSearchModelTransformer } from "@/features/publicCompanyLeadsFunnel/services/publicCompanyLeadsTransformer";
import { useEstablishmentLeads } from "@/features/establishmentLeadsFunnel/hooks/useEstablishmentLeads";
import { EstablishmentLeadsTransformer } from "@/features/establishmentLeadsFunnel/services/establishmentLeadsTransformer";
import { useExternalSupportTickets } from "@/features/support/external/hooks/useExternalSupportTickets";
import { ExternalSupportTicketsTransformer } from "@/features/support/external/services/externalSupportTicketsTransformer";
import { CompanyBillingEventTransformer } from "@/features/privateCompanies/services/companyBillingEventTransformer";
import { useBillingEvents } from "../hooks/queries/useBillingEvents";
import { EquipmentCategoriesTransformer } from "@/features/equipmentCategories/services/equipmentCategoriesTransformer";
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories";
import { EstablishmentBillingEventTransformer } from "@/features/establishments/services/establishmentBillingEventTransformer";

export const searchableModels: SearchableModel[] = [
  {
    label: 'Equipamentos',
    icon: CarFrontIcon,
    hook: useCompanyEquipments,
    mapper: EquipmentsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Empresas',
    icon: BriefcaseBusinessIcon,
    hook: useCompanies,
    mapper: CompaniesTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Rede credenciada (Estabelecimento)',
    icon: Building2Icon,
    hook: useEstablishmentCompanies,
    mapper: EstablishmentCompanyTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Usuários (Tcard)',
    icon: User2Icon,
    hook: useUsers,
    mapper: UsersTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Usuários (Clientes)',
    icon: User2Icon,
    hook: useCompanyUsers,
    mapper: CompanyUsersTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Usuários (Rede credenciada)',
    icon: User2Icon,
    hook: useEstablishmentUsers,
    mapper: EstablishmentUsersTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Cartões',
    icon: WalletCardsIcon,
    hook: useCards as SearchableModel['hook'],
    mapper: AllCardsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Faturas (A receber)',
    icon: FileTextIcon,
    hook: useClientBills,
    mapper: ClientBillsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Faturas (A pagar)',
    icon: FileTextIcon,
    hook: useEstablishmentBills,
    mapper: EstablishmentBillsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Transações (Clientes)',
    icon: DollarSign,
    hook: useBillingEvents,
    mapper: CompanyBillingEventTransformer.quickSearchModelTransformer,
    defaultFilters: {
      branchType: 'Client::CompanyBranch',
    },
  },
  {
    label: 'Transações (Rede credenciada)',
    icon: DollarSign,
    hook: useBillingEvents,
    mapper: EstablishmentBillingEventTransformer.quickSearchModelTransformer,
    defaultFilters: {
      branchType: 'Establishment::CompanyBranch',
    },
  },
  {
    label: 'Relatórios',
    icon: FileSpreadsheetIcon,
    hook: usePermittedReports,
    mapper: PermittedReportsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Impressoras de cartão',
    icon: PrinterIcon,
    hook: usePrinterMachines,
    mapper: PrinterMachinesTransformer.quickSearchModelTransformer,
  },
  {
    label: 'POS',
    icon: PlugIcon,
    hook: usePosMachines,
    mapper: PosTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Tarifas',
    icon: PiggyBankIcon,
    hook: useFeeTemplates,
    mapper: FeeTemplatesTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Produtos',
    icon: FileBoxIcon,
    hook: useProducts,
    mapper: ProductsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Categorias de equipamentos',
    icon: ForkliftIcon,
    hook: useEquipmentCategories,
    mapper: EquipmentCategoriesTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Categoria de produtos',
    icon: BoxesIcon,
    hook: useProductCategories,
    mapper: ProductCategoryTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Modelos de equipamentos',
    icon: CarFrontIcon,
    hook: useEquipmentModels,
    mapper: EquipmentModelsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Recolha de notas fiscais por transação',
    icon: CoinsIcon,
    hook: useBillingEventsWithFiscalDocumentRequirement,
    mapper: BillingEventsFiscalDocumentsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Recolha de notas fiscais por lote',
    icon: TablePropertiesIcon,
    hook: useBillingFiscalDocumentGroups,
    mapper: BillingFiscalDocumentGroupsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Funil de clientes privados',
    icon: BuildingIcon,
    hook: usePrivateCompanyLeads,
    mapper: privateCompanyLeadsQuickSearchModelTransformer,
  },
  {
    label: 'Funil de clientes públicos',
    icon: GlobeLockIcon,
    hook: usePublicCompanyLeads,
    mapper: publicCompanyLeadsQuickSearchModelTransformer,
  },
  {
    label: 'Funil de estabelecimento',
    icon: FuelIcon,
    hook: useEstablishmentLeads,
    mapper: EstablishmentLeadsTransformer.quickSearchModelTransformer,
  },
  {
    label: 'Chamados externos',
    icon: TicketIcon,
    hook: useExternalSupportTickets,
    mapper: ExternalSupportTicketsTransformer.quickSearchModelTransformer,
  },
];
